import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DealTicker from "@/components/DealTicker";
import { SectionBadge } from "@/components/Comparison";
import useSeo from "@/hooks/useSeo";
import { ArrowRight, Check, ShieldCheck, MapPin, Building2 } from "lucide-react";

/* -----------------------------------------------------------------------------
 * SEO landing template — powers both /business-loans/:city and /business-finance/:sector
 * -------------------------------------------------------------------------- */

const CITIES = {
  london:      { name: "London",      area: "the capital and Greater London",   sectors: "financial services · property · hospitality · tech" },
  manchester:  { name: "Manchester",  area: "Greater Manchester",               sectors: "tech · manufacturing · media · retail" },
  birmingham:  { name: "Birmingham",  area: "the West Midlands",                sectors: "manufacturing · automotive · logistics · professional services" },
  leeds:       { name: "Leeds",       area: "West Yorkshire",                   sectors: "financial services · digital · manufacturing · healthcare" },
  bristol:     { name: "Bristol",     area: "the South West",                   sectors: "aerospace · tech · hospitality · creative" },
  sheffield:   { name: "Sheffield",   area: "South Yorkshire",                  sectors: "manufacturing · engineering · construction · retail" },
  liverpool:   { name: "Liverpool",   area: "Merseyside",                       sectors: "tourism · maritime · life sciences · retail" },
  newcastle:   { name: "Newcastle",   area: "the North East",                   sectors: "energy · tech · manufacturing · professional services" },
  nottingham:  { name: "Nottingham",  area: "the East Midlands",                sectors: "life sciences · logistics · retail · hospitality" },
  leicester:   { name: "Leicester",   area: "the East Midlands",                sectors: "manufacturing · food · logistics · retail" },
  southampton: { name: "Southampton", area: "the South Coast",                  sectors: "maritime · aerospace · tourism · logistics" },
  cardiff:     { name: "Cardiff",     area: "South Wales",                      sectors: "financial services · media · tourism · life sciences" },
  glasgow:     { name: "Glasgow",     area: "central Scotland",                 sectors: "energy · engineering · financial services · tourism" },
  edinburgh:   { name: "Edinburgh",   area: "eastern Scotland",                 sectors: "financial services · tech · tourism · life sciences" },
  belfast:     { name: "Belfast",     area: "Northern Ireland",                 sectors: "tech · manufacturing · tourism · agri-food" },
  cambridge:   { name: "Cambridge",   area: "East Anglia",                      sectors: "tech · life sciences · biotech · professional services" },
  oxford:      { name: "Oxford",      area: "the Thames Valley",                sectors: "life sciences · tech · publishing · tourism" },
};

const SECTORS = {
  retail:               { name: "Retail",                purposes: ["Stock purchase", "Store fit-out", "Multi-site expansion", "Seasonal working capital"] },
  hospitality:          { name: "Hospitality",           purposes: ["Freehold acquisition", "Refurbishment", "Working capital", "Second-site expansion"] },
  construction:         { name: "Construction",          purposes: ["Plant & machinery", "Project working capital", "Vehicle finance", "Retention finance"] },
  manufacturing:        { name: "Manufacturing",         purposes: ["Equipment finance", "R&D innovation", "Facility expansion", "Export working capital"] },
  "professional-services": { name: "Professional Services", purposes: ["Office fit-out", "Partner buy-in", "Practice acquisition", "Working capital"] },
  healthcare:           { name: "Healthcare",            purposes: ["Practice acquisition", "Equipment finance", "Site expansion", "Working capital"] },
  technology:           { name: "Technology",            purposes: ["Growth capital", "R&D innovation", "Talent expansion", "Invoice finance"] },
  transport:            { name: "Transport & Logistics", purposes: ["Vehicle finance", "Depot expansion", "Fuel & fleet working capital", "Contract finance"] },
  property:             { name: "Property",              purposes: ["Commercial mortgage", "Bridging finance", "Development finance", "Portfolio refinance"] },
  wholesale:            { name: "Wholesale",             purposes: ["Stock finance", "Import finance", "Warehousing", "Working capital"] },
  agriculture:          { name: "Agriculture",           purposes: ["Farm asset finance", "Land purchase", "Diversification capital", "Seasonal working capital"] },
};

const CITY_LIST = Object.entries(CITIES).map(([slug, c]) => ({ slug, ...c }));
const SECTOR_LIST = Object.entries(SECTORS).map(([slug, s]) => ({ slug, ...s }));

export default function CitySectorLanding({ kind }) {
  const params = useParams();
  const slug = kind === "city" ? params.city : params.sector;
  const meta = kind === "city" ? CITIES[slug] : SECTORS[slug];

  const isCity = kind === "city";
  const displayName = meta?.name || "";
  const title = !meta ? "SwiftCash — UK Business Finance"
    : isCity ? `Business Loans in ${displayName} — from £5k to £500M · SwiftCash`
    : `${displayName} Business Finance — SwiftCash UK`;
  const description = !meta ? "UK business finance from £5,000 to £500,000,000."
    : isCity ? `UK business finance for ${displayName} companies. £5k to £500M across working capital, asset, commercial mortgage, invoice and growth. Indicative terms in hours.`
    : `Specialist ${displayName.toLowerCase()} business finance — from working capital to acquisition finance. Whole-of-market UK panel, indicative terms in hours.`;
  const path = !meta ? "/" : isCity ? `/business-loans/${slug}` : `/business-finance/${slug}`;
  useSeo({ title, description, path });

  if (!meta) return <Navigate to="/" replace />;

  const h1Lead = isCity ? `Business finance in` : `Finance built for`;
  const contextText = isCity
    ? `We match ${displayName}-based businesses — from ${meta.sectors} — with the right lender on our UK-wide panel. £5,000 to £500,000,000, tailored to how your business actually operates in ${meta.area}.`
    : `Every ${displayName.toLowerCase()} business is different. We work with UK lenders that specialise in the sector — so your deal is priced by underwriters who understand how ${displayName.toLowerCase()} companies really trade.`;

  return (
    <div className="bg-white text-navy min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative hero-grad pt-14 md:pt-20 pb-16 md:pb-20">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="inline-flex items-center gap-2 pl-1 pr-4 py-1 rounded-full bg-emerald-soft mb-6">
            <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-emerald text-white text-[10.5px] font-extrabold uppercase tracking-[0.08em]">
              {isCity ? <MapPin className="h-3 w-3" strokeWidth={2.6} /> : <Building2 className="h-3 w-3" strokeWidth={2.6} />}
              {isCity ? "UK regional finance" : "Sector-specific finance"}
            </span>
            <span className="text-[12.5px] font-bold text-emerald-3">£5k to £500M · Indicative terms in hours</span>
          </div>
          <h1 className="font-display font-extrabold text-[46px] sm:text-[64px] lg:text-[80px] leading-[1] tracking-[-0.035em] text-navy max-w-4xl">
            {h1Lead} <span className="text-emerald">{displayName}.</span>
          </h1>
          <p className="mt-6 text-[17px] md:text-[18px] leading-[1.6] text-navy/70 max-w-2xl font-medium">
            {contextText}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/apply" data-testid="landing-apply-btn"
              className="btn-shine group inline-flex items-center gap-2 h-14 px-8 rounded-xl bg-emerald text-white font-extrabold text-[15px] hover:bg-emerald-2 shadow-emerald">
              Get my tailored quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
            </Link>
            <Link to="/#pricing" className="inline-flex items-center gap-2 h-14 px-7 rounded-xl border border-navy/15 text-navy font-bold text-[14px] hover:bg-paper-warm">
              See indicative pricing
            </Link>
          </div>
        </div>
      </section>

      <DealTicker />

      {/* Purposes / typical use cases */}
      <section className="bg-paper-warm py-20 md:py-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <SectionBadge>{isCity ? `Popular in ${displayName}` : `Common ${displayName.toLowerCase()} deals`}</SectionBadge>
          <h2 className="font-display font-extrabold text-[36px] md:text-[48px] leading-[1.05] tracking-[-0.035em] text-navy mt-5 mb-10 max-w-3xl">
            {isCity
              ? <>What {displayName} businesses typically fund.</>
              : <>How the panel funds <span className="text-emerald">{displayName.toLowerCase()}</span> deals.</>}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {(isCity
              ? ["Working capital & cash flow", "Commercial mortgage & premises", "Equipment & vehicle finance", "Growth & acquisition finance"]
              : meta.purposes
            ).map((p, i) => (
              <div key={i} data-testid={`purpose-${i}`} className="rounded-xl bg-white border border-line p-5">
                <div className="flex items-start gap-3">
                  <span className="h-6 w-6 rounded-full bg-emerald flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="h-3.5 w-3.5 text-white" strokeWidth={3.2} />
                  </span>
                  <span className="text-[14.5px] text-navy font-bold leading-[1.5]">{p}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links to other pages for internal linking / SEO */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-2 gap-10">
            <div>
              <SectionBadge>All UK regions</SectionBadge>
              <h3 className="font-display font-extrabold text-[28px] md:text-[36px] tracking-[-0.03em] text-navy mt-4 mb-6">
                Business loans across the UK
              </h3>
              <div className="flex flex-wrap gap-2">
                {CITY_LIST.map((c) => (
                  <Link key={c.slug} to={`/business-loans/${c.slug}`} data-testid={`region-link-${c.slug}`}
                    className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-full border text-[13px] font-bold transition-colors
                      ${isCity && slug === c.slug ? "bg-navy text-white border-navy" : "bg-white border-line text-navy hover:border-navy/30 hover:bg-paper-warm"}`}>
                    <MapPin className="h-3 w-3" strokeWidth={2.4} /> {c.name}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <SectionBadge>All sectors</SectionBadge>
              <h3 className="font-display font-extrabold text-[28px] md:text-[36px] tracking-[-0.03em] text-navy mt-4 mb-6">
                Finance built for your sector
              </h3>
              <div className="flex flex-wrap gap-2">
                {SECTOR_LIST.map((s) => (
                  <Link key={s.slug} to={`/business-finance/${s.slug}`} data-testid={`sector-link-${s.slug}`}
                    className={`inline-flex items-center gap-1 px-3.5 py-2 rounded-full border text-[13px] font-bold transition-colors
                      ${!isCity && slug === s.slug ? "bg-navy text-white border-navy" : "bg-white border-line text-navy hover:border-navy/30 hover:bg-paper-warm"}`}>
                    <Building2 className="h-3 w-3" strokeWidth={2.4} /> {s.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust CTA */}
      <section className="hero-grad-dark text-white py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 dots-dark opacity-40 pointer-events-none" />
        <div className="max-w-3xl mx-auto px-5 md:px-8 text-center relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white font-mono text-[10.5px] uppercase tracking-[0.2em] font-bold mb-6">
            <ShieldCheck className="h-3 w-3" strokeWidth={2.6} /> No footprint · Indicative in hours
          </div>
          <h2 className="font-display font-extrabold text-[40px] md:text-[54px] leading-[1] tracking-[-0.035em]">
            {isCity
              ? <>Ready to fund your {displayName} <span className="text-emerald">business?</span></>
              : <>Ready to fund your <span className="text-emerald">{displayName.toLowerCase()}</span> deal?</>}
          </h2>
          <div className="mt-8">
            <Link to="/apply" data-testid="landing-final-cta"
              className="btn-shine group inline-flex items-center gap-2 h-14 px-8 rounded-xl bg-emerald text-white font-extrabold text-[15px] hover:bg-emerald-2 shadow-emerald">
              Get my tailored quote <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.4} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export { CITY_LIST, SECTOR_LIST };
